import { LoadingActionButton } from '@components/LoadingActionButton/LoadingActionButton';
import { useWindowSize } from '@lib/hooks/sizing/window-size';
import { PlanApproxNote } from '@lib/planFeatures';
import { handlePortalClick } from '@lib/purchase';
import { describeSubscriptionStatus } from '@lib/utils/planAccess';
import { CircularProgress } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { EnterpriseTierEntry } from '@pages/Pricing/components/EnterpriseTierEntry/EnterpriseTierEntry';
import { PlanFeatures } from '@pages/Pricing/components/PlanFeatures/PlanFeatures';
import { TierEntry } from '@pages/Pricing/components/TierEntry/TierEntry';
import { TierGroup } from '@pages/Pricing/components/TierGroup/TierGroup';
import { billingStore } from '@stores/Billing';
import { paywallStore } from '@stores/Paywall';
import { trackingStore } from '@stores/Tracking';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { PaywallBar } from './PaywallBar';
import css from './PaywallPage.module.scss';

const SUPPORT_EMAIL = 'support@starzero.ai';

// `TierGroup` stacks its cards into one column below this width; the stacked layout
// collapses the credit breakdowns the same way the Pricing page does.
const STACKED_BELOW_PX = 900;

/**
 * The plans a walled account picks from. Same cards as the Pricing page; the difference is
 * the checkout runs in this tab and the page waits for the subscription to come back.
 */
const PlanPicker = observer(() => {
  const [windowWidth] = useWindowSize();
  const isStacked = windowWidth < STACKED_BELOW_PX;
  const [expandedPlans, setExpandedPlans] = useState<Set<string>>(new Set());
  const expandPlan = (id: string) =>
    setExpandedPlans((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

  const scrollToApproxNote = () => {
    document
      .getElementById('amounts-approximate')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const plans = billingStore.availableSubscriptions;

  if (plans.length === 0) {
    return (
      <div className={css.centered}>
        <CircularProgress size={32} sx={{ color: '#9d4edd' }} />
      </div>
    );
  }

  return (
    <>
      <TierGroup gap={12} verticalAlign="start">
        {plans.map((plan, index) => {
          // Index 1 is the recommended tier, as on the Pricing page; a lone plan is it too.
          const isRecommended = index === 1 || (index === 0 && plans.length === 1);
          const showBreakdowns = !isStacked || isRecommended || expandedPlans.has(plan.id);
          return (
            <TierEntry
              key={plan.id}
              product={plan}
              productType="subscription"
              highlighted={isRecommended}
              badge={isRecommended ? 'Best value · most credits per dollar' : undefined}
              ctaProps={{
                text: `Get ${plan.name}`,
                variant: isRecommended ? 'primary-cta' : 'secondary-cta',
                onClick: () => paywallStore.checkout(plan.id, 'paywall'),
              }}
            >
              <PlanFeatures
                product={plan}
                expanded={showBreakdowns}
                onExpand={() => expandPlan(plan.id)}
                onApproxClick={scrollToApproxNote}
              />
            </TierEntry>
          );
        })}
        <EnterpriseTierEntry source="paywall" />
      </TierGroup>
      <p className={css.approxNote} id="amounts-approximate">
        <span className={css.approxMarker} aria-hidden="true">
          *
        </span>
        <span>
          <PlanApproxNote />
        </span>
      </p>
    </>
  );
});

/** A subscription is attached but no longer pays; only the Stripe portal can repair it. */
const LapsedNotice = observer(() => {
  const subscription = billingStore.data?.subscription;
  const planName = subscription?.product?.name ?? 'StarZero';
  const status = subscription
    ? describeSubscriptionStatus(subscription.status)
    : 'no longer active';

  return (
    <div className={css.notice}>
      <h1 className={css.title}>Your subscription needs attention</h1>
      <p className={css.body}>
        Your {planName} plan is {status}. Fix it in the billing portal to keep using StarZero.
      </p>
      <div className={css.noticeActions}>
        <LoadingActionButton
          variant="primary-cta"
          onClick={() => handlePortalClick('paywall')}
          loadingText="Opening the billing portal..."
        >
          Open billing portal
        </LoadingActionButton>
        <LoadingActionButton
          variant="secondary"
          onClick={paywallStore.recheck}
          loadingText="Checking..."
        >
          I&apos;ve fixed it, check again
        </LoadingActionButton>
      </div>
    </div>
  );
});

export const PaywallPage = observer(() => {
  const access = paywallStore.access;
  const confirmation = paywallStore.confirmation;

  useEffect(() => {
    billingStore.ensureSubscriptionsLoaded().catch((error) => {
      console.error('Paywall: failed to load subscriptions', error);
    });
  }, []);

  // A view is a wall the user can act on. The return from a checkout also mounts this page,
  // in the confirming state; counting that would make every conversion two views.
  useEffect(() => {
    if (confirmation !== 'idle') return;
    if (access === 'no-plan') trackingStore.paywallViewed({ variant: 'plans' });
    if (access === 'lapsed') trackingStore.paywallViewed({ variant: 'portal' });
  }, [access, confirmation]);

  // Entitled accounts have nothing to do here; this also carries a user through once a
  // checkout is confirmed, since confirming re-latches the verdict.
  if (!paywallStore.isWalled) return <Navigate to="/workspace" replace />;

  return (
    <div className={css.page}>
      <PaywallBar />
      <main className={css.main}>
        {confirmation === 'confirming' ? (
          <div className={css.notice}>
            <CircularProgress size={32} sx={{ color: '#9d4edd' }} />
            <h1 className={css.title}>Confirming your subscription</h1>
            <p className={css.body}>This usually takes a few seconds.</p>
            {/* The browser's back button from Stripe lands here too, with nothing to
                confirm; give that user a way out before the wait runs its course. */}
            <button
              type="button"
              className={css.textAction}
              onClick={paywallStore.cancelPendingCheckout}
            >
              Didn&apos;t finish checkout? Back to plans
            </button>
          </div>
        ) : access === 'lapsed' ? (
          <LapsedNotice />
        ) : (
          <>
            {confirmation === 'timed-out' && (
              <div className={css.notice}>
                <h1 className={css.title}>No plan on your account yet</h1>
                <p className={css.body}>
                  We couldn&apos;t confirm a plan on your account. If you completed the payment, it
                  can take a few minutes to arrive: check again in a moment, or write to us if it
                  doesn&apos;t show up.
                </p>
                <div className={css.noticeActions}>
                  <LoadingActionButton
                    variant="primary-cta"
                    onClick={paywallStore.recheck}
                    loadingText="Checking..."
                  >
                    Check again
                  </LoadingActionButton>
                  <ActionButton
                    variant="secondary"
                    onClick={() => {
                      window.location.href = `mailto:${SUPPORT_EMAIL}?subject=Subscription%20not%20activated`;
                    }}
                  >
                    Contact support
                  </ActionButton>
                </div>
              </div>
            )}
            <div className={css.header}>
              <h1 className={css.title}>Pick your plan</h1>
              <p className={css.body}>
                StarZero needs a plan on your account to get going. Upgrade or change it any time.
              </p>
            </div>
            <PlanPicker />
          </>
        )}
      </main>
    </div>
  );
});
